import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./CycleInsightsPanel.css";

import aboutIcon from "@/assets/about.png";
import ellipseFert from "@/assets/icons/Ellipse 1503 (1).png";
import ellipsePeriod from "@/assets/icons/Ellipse 1504 (1).png";
import iconArrow from "@/assets/icons/Icon-Arrow.png";
import iconArrow1 from "@/assets/icons/Icon-Arrow1.png";

import days28Icon from "@/assets/icons/28day.png";
import periodIcon from "@/assets/icons/Period.png";

/* ✅ Icône Edit ajoutée */
const PencilIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M15.8805 5.10929C16.277 4.71285 16.4998 4.17514 16.4999 3.61443C16.4999 3.05372 16.2773 2.51594 15.8808 2.11941C15.4844 1.72288 14.9467 1.50007 14.386 1.5C13.8253 1.49993 13.2875 1.7226 12.8909 2.11904L2.88145 12.1308C2.70732 12.3044 2.57854 12.5182 2.50645 12.7533L1.5157 16.0173C1.49632 16.0821 1.49485 16.151 1.51146 16.2167C1.52807 16.2823 1.56214 16.3422 1.61005 16.39C1.65796 16.4379 1.71792 16.4718 1.78357 16.4883C1.84922 16.5048 1.91812 16.5033 1.98295 16.4838L5.2477 15.4938C5.48258 15.4224 5.69633 15.2944 5.8702 15.121L15.8805 5.10929Z"
      stroke="#546881"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.25 3.75L14.25 6.75"
      stroke="#546881"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* =========================
   Date helpers
========================= */
const pad2 = (n) => String(n).padStart(2, "0");

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const sameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const ymdKey = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const mondayIndex = (jsDay) => (jsDay + 6) % 7;

function buildMonthGrid(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = mondayIndex(firstOfMonth.getDay());

  const totalCells = offset + daysInMonth;
  const totalWeeks = Math.ceil(totalCells / 7);
  const cellsCount = totalWeeks * 7;

  const grid = [];
  const prevMonthLastDay = new Date(year, month, 0).getDate();

  for (let i = 0; i < cellsCount; i++) {
    const cellIndex = i - offset + 1;
    let cellDate;
    let currentMonth = true;

    if (cellIndex < 1) {
      currentMonth = false;
      const day = prevMonthLastDay + cellIndex;
      cellDate = new Date(year, month - 1, day);
    } else if (cellIndex > daysInMonth) {
      currentMonth = false;
      const day = cellIndex - daysInMonth;
      cellDate = new Date(year, month + 1, day);
    } else {
      cellDate = new Date(year, month, cellIndex);
    }

    grid.push({
      date: cellDate,
      day: cellDate.getDate(),
      currentMonth,
      key: ymdKey(cellDate),
    });
  }

  const weeks = [];
  for (let w = 0; w < totalWeeks; w++) weeks.push(grid.slice(w * 7, w * 7 + 7));
  return weeks;
}

/* =========================
   Cycle calculation
========================= */
function computeCycleSets({ lastPeriodStart, cycleLength, periodLength, windowStart, windowEnd }) {
  const periodSet = new Set();
  const fertSet = new Set();
  const peakSet = new Set();

  // If any required input is missing, do not fabricate cycles/predictions
  if (!cycleLength || !periodLength || !lastPeriodStart) {
    return { periodSet, fertSet, peakSet };
  }

  const start = startOfDay(lastPeriodStart);

  const safeCycle = Math.max(15, Math.min(60, Number(cycleLength)));
  const safePeriod = Math.max(2, Math.min(14, Number(periodLength)));

  const maxCycles = 60;

  for (let c = 0; c < maxCycles; c++) {
    const cycleStart = addDays(start, c * safeCycle);
    if (cycleStart > windowEnd) break;

    for (let i = 0; i < safePeriod; i++) {
      const d = addDays(cycleStart, i);
      if (d >= windowStart && d <= windowEnd) periodSet.add(ymdKey(d));
    }

    const peak = addDays(cycleStart, safeCycle - 14);
    if (peak >= windowStart && peak <= windowEnd) peakSet.add(ymdKey(peak));

    const fertStart = addDays(peak, -5);
    const fertEnd = addDays(peak, 1);

    for (let d = startOfDay(fertStart); d <= fertEnd; d = addDays(d, 1)) {
      if (d >= windowStart && d <= windowEnd) fertSet.add(ymdKey(d));
    }
  }

  for (const k of periodSet) {
    fertSet.delete(k);
    peakSet.delete(k);
  }
  for (const k of peakSet) fertSet.delete(k);

  return { periodSet, fertSet, peakSet };
}

function computePillEdges(weeks, periodSet, fertSet) {
  const meta = new Map();
  const typeOf = (key) => (periodSet.has(key) ? "per" : fertSet.has(key) ? "fert" : null);

  for (const week of weeks) {
    for (let i = 0; i < week.length; i++) {
      const key = week[i].key;
      const t = typeOf(key);
      if (!t) continue;

      const prev = i > 0 ? typeOf(week[i - 1].key) : null;
      const next = i < 6 ? typeOf(week[i + 1].key) : null;

      const isStart = prev !== t;
      const isEnd = next !== t;
      const isSingle = isStart && isEnd;

      meta.set(key, { type: t, isStart, isEnd, isSingle });
    }
  }
  return meta;
}

/* =========================
   UI blocks
========================= */
const StatCard = ({ icon, title, value, unit }) => (
  <div className="cip-stat">
    <div className="cip-stat__left">
      <div className="cip-stat__icon">
        <img src={icon} alt="" />
      </div>
      <div className="cip-stat__meta">
        <div className="cip-stat__title">{title}</div>
      </div>
    </div>
    <div className="cip-stat__right">
      <div className="cip-stat__value">
        <span className="cip-stat__num">{value}</span>
        <span className="cip-stat__unit">{unit}</span>
      </div>
    </div>
  </div>
);

const SummaryCard = ({ label, day, month }) => (
  <div className="cip-sum">
    <div className="cip-sum__label">{label}</div>
    <div className="cip-sum__date">
      <div className="cip-sum__day">{day}</div>
      <div className="cip-sum__month">{month}</div>
    </div>
  </div>
);

export default function CycleInsightsPanel({
  lastPeriodStart,
  cycleLength,
  periodLength,
  onPeakChange,
  onEditClick,
}) {
  const { t, i18n } = useTranslation("cycle");
  const currentLang = i18n.language;
  
  const [selectedStart, setSelectedStart] = useState(null);

  const baseStart = useMemo(() => {
    const src = selectedStart || lastPeriodStart;
    return src ? startOfDay(new Date(src)) : null;
  }, [selectedStart, lastPeriodStart]);

  // Consider data available only when we have a base start AND length values
  // This prevents fabricating cycles when start exists but lengths are unknown
  const hasData = Boolean(baseStart && cycleLength && periodLength);

  const [viewDate, setViewDate] = useState(() => {
    const now = new Date();
    return startOfDay(new Date(now.getFullYear(), now.getMonth(), 1));
  });

  const weekdays = useMemo(() => [
    t("cycleInsights.weekdays.mon"),
    t("cycleInsights.weekdays.tue"),
    t("cycleInsights.weekdays.wed"),
    t("cycleInsights.weekdays.thu"),
    t("cycleInsights.weekdays.fri"),
    t("cycleInsights.weekdays.sat"),
    t("cycleInsights.weekdays.sun")
  ], [t]);

  const weeks = useMemo(() => buildMonthGrid(viewDate), [viewDate]);

  const { windowStart, windowEnd } = useMemo(() => {
    const first = weeks?.[0]?.[0]?.date || new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const last =
      weeks?.[weeks.length - 1]?.[6]?.date ||
      new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    return { windowStart: startOfDay(first), windowEnd: startOfDay(last) };
  }, [weeks, viewDate]);

  const { periodSet, fertSet, peakSet } = useMemo(() => {
    if (!hasData) return { periodSet: new Set(), fertSet: new Set(), peakSet: new Set() };

    return computeCycleSets({
      lastPeriodStart: baseStart,
      cycleLength: Number(cycleLength),
      periodLength: Number(periodLength),
      windowStart,
      windowEnd,
    });
  }, [hasData, baseStart, cycleLength, periodLength, windowStart, windowEnd]);

  const pillMeta = useMemo(
    () => computePillEdges(weeks, periodSet, fertSet),
    [weeks, periodSet, fertSet]
  );

  const monthName = useMemo(() => {
    return viewDate.toLocaleDateString(currentLang, { month: "long" });
  }, [viewDate, currentLang]);

  const viewYear = viewDate.getFullYear();

  const shift = (dir) => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();
    setViewDate(new Date(y, m + (dir === "next" ? 1 : -1), 1));
  };

  const peakISO = useMemo(() => {
    // Require both a base start and a cycle length to compute the peak
    if (!hasData || !cycleLength || !baseStart) return null;
    const safeCycle = Math.max(15, Math.min(60, Number(cycleLength)));
    const peak = addDays(baseStart, safeCycle - 14);
    return peak.toISOString();
  }, [hasData, baseStart, cycleLength]);

  useEffect(() => {
    if (!onPeakChange) return;
    onPeakChange(peakISO);
  }, [peakISO, onPeakChange]);

  const getDayClass = (cell) => {
    const k = cell.key;

    let cls = "cip-day";
    if (!cell.currentMonth) cls += " is-other";

    const meta = pillMeta.get(k);
    if (meta?.type === "per") cls += " is-per";
    if (meta?.type === "fert") cls += " is-fert";
    if (peakSet.has(k)) cls += " is-peak";

    if (meta?.isSingle) cls += " is-single";
    else {
      if (meta?.isStart) cls += " is-start";
      if (meta?.isEnd) cls += " is-end";
    }

    if (baseStart && sameDay(cell.date, baseStart)) cls += " is-selected";

    return cls;
  };

  const onPickDate = (cell) => {
    const picked = startOfDay(cell.date);
    setSelectedStart(picked);

    if (cell.date.getMonth() !== viewDate.getMonth() || cell.date.getFullYear() !== viewDate.getFullYear()) {
      setViewDate(startOfDay(new Date(cell.date.getFullYear(), cell.date.getMonth(), 1)));
    }
  };

  const lastSummary = useMemo(() => {
    // If we don't have a base start or lengths, return placeholders
    if (!hasData || cycleLength == null || periodLength == null) {
      return {
        startDay: "--",
        startMonth: "--",
        endDay: "--",
        endMonth: "--",
        durationDay: "--",
        durationMonth: t("cycleInsights.days"),
        peakDay: "--",
        peakMonth: "--",
      };
    }

    const start = baseStart;
    const end = addDays(start, Number(periodLength) - 1);

    const safeCycle = Math.max(15, Math.min(60, Number(cycleLength)));
    const peak = addDays(start, safeCycle - 14);

    const fmtMonth = (d) => d.toLocaleDateString(currentLang, { month: "short" });

    return {
      startDay: pad2(start.getDate()),
      startMonth: fmtMonth(start),
      endDay: pad2(end.getDate()),
      endMonth: fmtMonth(end),
      durationDay: pad2(Number(periodLength)),
      durationMonth: t("cycleInsights.days"),
      peakDay: pad2(peak.getDate()),
      peakMonth: fmtMonth(peak),
    };
  }, [hasData, baseStart, cycleLength, periodLength, t, currentLang]);

  const avgCycle = hasData ? pad2(Number(cycleLength)) : "--";
  const avgPeriod = hasData ? pad2(Number(periodLength)) : "--";
  const regularityPct = hasData ? 85 : 0;

  return (
    <section className="cip">
      <div className="cip-calcard">
     <header className="cip-calhead">
  <div className="cip-nav">
     {/* ✅ Flèche droite (prev) */}
    <button className="cip-navbtn" type="button" onClick={() => shift("prev")} aria-label={t("cycleInsights.prevMonth")}>
      <img src={iconArrow} alt="" width="12" height="12" />
    </button>
    {/* ✅ Month/Year au centre */}
    <div className="cip-monthyear">
      {monthName} {viewYear}
    </div>
     {/* ✅ Flèche gauche (next) */}
    <button className="cip-navbtn" type="button" onClick={() => shift("next")} aria-label={t("cycleInsights.nextMonth")}>
      <img src={iconArrow1} alt="" width="12" height="12" />
    </button>
    
  </div>

  {/* ✅ Bouton Edit à droite */}
  {onEditClick && (
    <button
      type="button"
      className="cip-edit-btn"
      onClick={onEditClick}
      aria-label="Edit cycle"
      title="Edit"
    >
      <PencilIcon />
    </button>
  )}
</header>

        <div className="cip-weekdays">
          {weekdays.map((d, i) => (
            <div key={i} className="cip-weekday">
              {d}
            </div>
          ))}
        </div>

        <div className="cip-grid">
          {weeks.map((week, wi) => (
            <div className="cip-week" key={wi}>
              {week.map((cell, ci) => (
                <div
                  key={`${wi}-${ci}-${cell.key}`}
                  className={getDayClass(cell)}
                  role="button"
                  tabIndex={0}
                  onClick={() => onPickDate(cell)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onPickDate(cell);
                  }}
                  aria-label={t("cycleInsights.dayAriaLabel", { day: cell.day })}
                >
                  {pad2(cell.day)}
                </div>
              ))}
            </div>
          ))}
        </div>

        {baseStart && (
          <div className="cip-selectedHint">
            {t("cycleInsights.rulesCalculatedFrom")}: <b>{ymdKey(baseStart)}</b>
            <button className="cip-selectedClear" type="button" onClick={() => setSelectedStart(null)}>
              {t("cycleInsights.reset")}
            </button>
          </div>
        )}
      </div>

      <div className="cip-toprow">
        <div className="cip-titlebox">
          <img src={aboutIcon} alt="" className="cip-abouticon" />
          <span className="cip-title">{t("cycleInsights.aboutYourCycle")}</span>
        </div>

        <div className="cip-legend">
          <img src={ellipseFert} alt="" />
          <span className="cip-title">{t("cycleInsights.fertility")}</span>
          <img src={ellipsePeriod} alt="" />
          <span className="cip-title">{t("cycleInsights.period")}</span>
        </div>
      </div>

      <div className="cip-stats">
        <StatCard 
          icon={days28Icon} 
          title={
            <>
              {t("cycleInsights.averageCycleLength.line1")} <br /> 
              {t("cycleInsights.averageCycleLength.line2")}
            </>
          } 
          value={avgCycle} 
          unit={t("cycleInsights.days")} 
        />
        <StatCard 
          icon={periodIcon} 
          title={t("cycleInsights.averagePeriodLength")} 
          value={avgPeriod} 
          unit={t("cycleInsights.days")} 
        />
      </div>
    </section>
  );
}
